"use client";

import { useRef } from "react";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { PageCompetitionSchema } from "@/sanity/pages/PageCompetitionTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { CTAButton } from "@/shadcn/ui/button";
import { motion, useScroll, useTransform } from "framer-motion";

import bg from "@/public/global/bg-art.jpg";
import knight from "@/public/global/knight.png";
import ChipSerif from "@/components/global/ui/ChipSerif";
import Countdown from "@/components/global/countdown/Countdown";
import doubleArrow from "@/public/global/svg/doubleArrow.svg";

export default function HeroSection({
  data,
  className,
  showTopBanner,
}: {
  data: PageCompetitionSchema;
  className?: string;
  showTopBanner?: boolean;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const ySolana = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);
  const yKnight = useTransform(scrollYProgress, [0, 1], ["-2%", "2%"]);

  return (
    <section
      id="hero-section"
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[13rem] lg:pt-[18.75rem]">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-10 lg:opacity-[.36]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container flex relative z-10 flex-col items-center px-[2.38rem] pb-[3.25rem] lg:pb-[15.12rem]">
          {data?.heroChip && (
            <ChipSerif text={data.heroChip} />
          )}

          {data?.heroText && (
            <div className="richtext prose-h2:text-[5.5rem] lg:prose-h2:text-[6rem] prose-p:text-[1.75rem] lg:prose-p:text-[2rem] max-w-[40.375rem] lg:max-w-none">
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTAButton className="mt-[3rem] w-[min(34.875rem,100%)] lg:w-auto" data={data.heroCTA} />
          )}

          <div className="w-[min(84rem,100%)] relative flex flex-col items-center mt-[7rem]">
            <div className="hidden md:block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[110%] lg:w-[91.75rem] h-[calc(100%-14rem)] bg-[#103037] rounded-[1.25rem] lg:rounded-[2.25rem]" />
            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-[calc(100%-3rem)] lg:h-[calc(100%-7rem)] bg-[#489268]/[.55] backdrop-blur-[2rem] rounded-[1.25rem] lg:rounded-[2.25rem]" />

            <div className="relative !w-[min(74.875rem,100%)] flex flex-col gap-[2rem] items-center justify-center bg-[#103037] rounded-[2.875rem] p-[1.25rem]">
              <div className="flex flex-col gap-[2.75rem] items-center bg-white/10 rounded-[2.25rem] py-[3.13rem] px-[2.25rem] lg:p-[2.88rem] w-full">
                <div className="flex items-center justify-center w-full gap-[.5rem] lg:gap-[2.5rem] mb-[1rem]">
                  {data?.heroArrowText && (
                    <>
                      <Image src={doubleArrow} alt="" />
                      <span className="text-white text-center text-[1.5rem] lg:text-[1.75rem] uppercase leading-none tracking-[-0.0525rem] flex-shrink-0">{data.heroArrowText}</span>
                      <Image src={doubleArrow} alt="" style={{ transform: 'scaleX(-1)' }} />
                    </>
                  )}
                </div>

                <div className="flex flex-row items-end gap-[1rem]">
                  <div className="border border-white/20 rounded-full py-[1rem] px-[1.12rem] my-auto">
                    <svg className="w-[2.99238rem] h-[3.08888rem]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 25" fill="none">
                      <path fillRule="evenodd" clip-rule="evenodd" d="M14.4046 13.2833C14.2686 13.2947 13.5658 13.3417 11.9982 13.3417C10.7513 13.3417 9.86605 13.2998 9.55547 13.2833C4.73693 13.0458 1.14031 12.1058 1.14031 10.9803C1.14031 9.85483 4.73693 8.91609 9.55547 8.67474V12.3471C9.87058 12.3725 10.7729 12.4322 12.0197 12.4322C13.5159 12.4322 14.2652 12.3624 14.4001 12.3484V8.67728C19.2084 8.91736 22.7971 9.85737 22.7971 10.9803C22.7971 12.1032 19.2095 13.0432 14.4001 13.282L14.4046 13.2833ZM14.4046 8.29747V5.01125H21.115V0H2.8451V5.01125H9.55433V8.2962C4.10103 8.57693 0 9.78751 0 11.2382C0 12.6888 4.10103 13.8981 9.55433 14.1801V24.7108H14.4035V14.1763C19.8443 13.8956 23.9385 12.6863 23.9385 11.2369C23.9385 9.78751 19.8477 8.5782 14.4035 8.2962L14.4046 8.29747Z" fill="white"/>
                    </svg>
                  </div>
                  <h2 className="text-[4.75rem] lg:text-[6.75rem] font-[500] tracking-normal !text-white lg:leading-[90%] mb-0">{data.heroBigNumber}</h2>
                  <span className="text-[1.09075rem] text-white font-[700] uppercase mb-[1rem]">USDT</span>
                </div>

                <div className="richtext w-full prose-p:text-[1.5rem] prose-p:tracking-normal prose-p:text-white prose-p:uppercase prose-p:mb-0">
                  {data?.heroSmallText && (
                    <PortableText
                      value={data.heroSmallText}
                      components={richTextComponent}
                    />
                  )}
                </div>
              </div>
              {data?.toggleCountdown && data?.countdown && (
                <div className="w-full border border-white/[16%] rounded-[2.25rem] [&_.countdownUnitItem]:!border-white/[16%]">
                  <Countdown dateTimeString={data?.countdown} lang={(data?.countdownLang ?? "de") as "de" | "en"} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
